import { useRef, useState } from 'react';

import facebook from 'my-core/facebook';
import { onlineCourseShareActivityUrlCopied } from 'my-core/gtm-events';

import ErrorMessage from 'my-elements/ErrorMessage';

import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { faFacebook, faFacebookMessenger } from '@fortawesome/free-brands-svg-icons';
import { faCheckCircle, faTimes } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootCss = ({ palette, spacing }) => ({
  '.SAD-header': {
    padding: spacing(5, 4, 2),
  },
  '.SAD-closeBtn': {
    position: 'absolute',
    top: spacing(0.5),
    right: spacing(0.5),
  },

  '.SAD-details': {
    padding: spacing(3, 4, 4),
    backgroundColor: palette.background.default,
    minHeight: 100,
  },
  '.SAD-loading': {
    textAlign: 'center',
  },

  '.SAD-copyLinkWrapper': {
    display: 'flex',
    alignItems: 'center',
    marginBottom: spacing(3),
  },
  '.SAD-copyLinkTextField': {
    marginRight: spacing(1),
    '& > div': { backgroundColor: palette.background.paper },
  },
  '.SAD-copyLinkInput': {
    padding: spacing(1.5, 2),
  },
  '.SAD-copyLinkAction': {
    flex: '0 0 100px',
  },

  '.SAD-socialButtons': {
    display: 'flex',
    justifyContent: 'center',
  },
  '.SAD-facebookButton': {
    color: palette.thirdParty.facebook.main,
  },
});

export default function ShareActivityDialog({ error, fetching, onClose, onlineCourse, open, share }) {
  const shareURL = `${window?.location?.origin}/shares/${share?.token_id}`;
  const shareLinkRef = useRef();
  const [isCopied, setIsCopied] = useState(false);
  function copyShareLink() {
    shareLinkRef.current.select();
    document.execCommand('copy');
    onlineCourseShareActivityUrlCopied(onlineCourse);
    setIsCopied(true);
  }

  return (
    <Dialog css={rootCss} onClose={onClose} open={open}>
      <div className="SAD-header">
        <IconButton className="SAD-closeBtn" onClick={onClose} size="small">
          <FontAwesomeIcon fixedWidth icon={faTimes} size="lg" />
        </IconButton>
        <Typography align="center" gutterBottom variant="h5">
          Share {onlineCourse?.course_code} Activity
        </Typography>
        <Typography align="center">
          Give a friend <strong>free</strong> access to this activity by sharing the link below
        </Typography>
      </div>
      <div className="SAD-details">
        {fetching ?
          <div className="SAD-loading">
            <CircularProgress />
          </div>
        : error ?
          <ErrorMessage error={error} />
        : <>
            <Typography align="center" gutterBottom sx={{ display: 'block' }} variant="overline">
              copy this url to share:
            </Typography>
            <div className="SAD-copyLinkWrapper">
              <TextField
                className="SAD-copyLinkTextField"
                fullWidth
                readOnly
                slotProps={{ htmlInput: { ref: shareLinkRef, className: 'SAD-copyLinkInput' } }}
                value={shareURL}
              />
              <div className="SAD-copyLinkAction">
                {!isCopied ?
                  <Button fullWidth onClick={copyShareLink} variant="contained">
                    Copy
                  </Button>
                : <Typography align="center" color="primary">
                    <FontAwesomeIcon icon={faCheckCircle} /> Copied!
                  </Typography>
                }
              </div>
            </div>
            <Typography align="center" gutterBottom sx={{ display: 'block' }} variant="overline">
              or share on:
            </Typography>
            <div className="SAD-socialButtons">
              <IconButton className="SAD-facebookButton" onClick={handleFacebookShare} size="small">
                <FontAwesomeIcon fixedWidth icon={faFacebook} size="lg" />
              </IconButton>
              <IconButton className="SAD-facebookButton" onClick={handleFacebookMessage} size="small">
                <FontAwesomeIcon fixedWidth icon={faFacebookMessenger} size="lg" />
              </IconButton>
            </div>
          </>
        }
      </div>
    </Dialog>
  );

  function handleFacebookShare() {
    facebook.ui(
      {
        method: 'share',
        href: shareURL,
        quote: 'Course-specific video lessons. Difficult concepts, simplified',
        hashtag: '#studywize',
      },
      // this._handleShareResponse
    );
  }

  function handleFacebookMessage() {
    facebook.ui(
      {
        method: 'send',
        link: shareURL,
      },
      // this._handleShareResponse
    );
  }
}
